import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Toaster } from 'sonner';
import { useEffect } from 'react';
import { ensureServiceWorker } from '@/lib/webpush';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Unsubscribe from '@/pages/Unsubscribe';
import AlertsList from '@/pages/AlertsList';
import AlertDetail from '@/pages/AlertDetail';
import Dashboard from '@/pages/Dashboard';
import Settings from '@/pages/Settings';
import Notifications from '@/pages/Notifications';
import Admin from '@/pages/Admin';
import Noc from '@/pages/Noc';
import CameraNoc from '@/pages/CameraNoc';
import VmsNoc from '@/pages/VmsNoc';
import NocScreens from '@/pages/NocScreens';
import NocPair from '@/pages/NocPair';
import Restrictions from '@/pages/Restrictions';
import Team from '@/pages/Team';
import Loads from '@/pages/Loads';
import Status from '@/pages/Status';
import OversizeLoads from '@/pages/OversizeLoads';
import Help from '@/pages/Help';
import WhatsNew from '@/pages/WhatsNew';
import LiveMap from '@/pages/LiveMap';
import Track from '@/pages/Track';

function Protected({ children, admin = false, business = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) {
    // NOC/CCTV wall TVs can't do the Turnstile login — send them to the kiosk pairing screen instead.
    const dest = business ? `/noc/pair?to=${encodeURIComponent(loc.pathname)}` : `/login?to=${encodeURIComponent(loc.pathname)}`;
    return <Navigate to={dest} replace />;
  }
  if (admin && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  if (business && user.role !== 'admin' && user.plan !== 'business') return <Navigate to="/dashboard" replace />;
  return children;
}

import IosPushHelp from '@/components/IosPushHelp';
import InstallPrompt from '@/components/InstallPrompt';
import PushDevicePrompt from '@/components/PushDevicePrompt';

function ServiceWorkerInit() {
  useEffect(() => {
    ensureServiceWorker().catch(() => {});
  }, []);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ServiceWorkerInit />
        <IosPushHelp />
        <InstallPrompt />
        <PushDevicePrompt />
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          <Route path="/alerts" element={<AlertsList />} />
          <Route path="/alerts/:id" element={<AlertDetail />} />
          <Route path="/status" element={<Status />} />
          <Route path="/oversize" element={<OversizeLoads />} />
          <Route path="/help" element={<Help />} />
          <Route path="/whats-new" element={<WhatsNew />} />
          <Route path="/map" element={<LiveMap />} />
          <Route path="/track/:token" element={<Track />} />
          <Route path="/noc" element={<Protected business><Noc /></Protected>} />
          <Route path="/noc/cameras" element={<Protected business><CameraNoc /></Protected>} />
          <Route path="/noc/vms" element={<Protected business><VmsNoc /></Protected>} />
          <Route path="/noc/screens" element={<Protected business><NocScreens /></Protected>} />
          <Route path="/noc/pair" element={<NocPair />} />

          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/restrictions" element={<Protected><Restrictions /></Protected>} />
          <Route path="/loads" element={<Protected><Loads /></Protected>} />
          <Route path="/team" element={<Protected><Team /></Protected>} />

          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
